import * as React from 'react';
import { cn } from '../../lib/utils';

const TabsContext = React.createContext({ value: '', onValueChange: () => {} });

const Tabs = ({ value, defaultValue, onValueChange, className, children, ...props }) => {
  const [controlledValue, setControlledValue] = React.useState(defaultValue || '');
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : controlledValue;

  const handleValueChange = React.useCallback(
    (newValue) => {
      if (!isControlled) setControlledValue(newValue);
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={cn('w-full', className)} data-value={currentValue} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};
Tabs.displayName = 'Tabs';

const TabsList = React.forwardRef(({ className, variant = 'default', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center text-text-secondary',
      variant === 'pill'
        ? 'inline-flex gap-1 rounded-pill border border-border-light bg-background-secondary p-1'
        : 'inline-flex h-10 items-center gap-1 rounded-sm border border-border-light bg-background-secondary p-1',
      className
    )}
    {...props}
  />
));
TabsList.displayName = 'TabsList';

const TabsTrigger = React.forwardRef(
  ({ value, className, children, icon: Icon, variant = 'default', ...props }, ref) => {
    const context = React.useContext(TabsContext);
    const isActive = context.value === value;
    const isPill = variant === 'pill';

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={isActive}
        data-state={isActive ? 'active' : 'inactive'}
        className={cn(
          'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          isPill ? 'rounded-pill px-4 py-1.5' : 'rounded-sm px-4 py-2',
          isActive && 'border border-border-light bg-background-elevated text-text-primary shadow-sm',
          !isActive && 'text-text-secondary hover:text-text-primary hover:bg-canvas-soft-2',
          className
        )}
        onClick={() => context.onValueChange(value)}
        {...props}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
        {children}
      </button>
    );
  }
);
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef(({ value, className, children, ...props }, ref) => {
  const context = React.useContext(TabsContext);
  if (context.value !== value) return null;
  return (
    <div
      ref={ref}
      role="tabpanel"
      className={cn('w-full mt-4 focus-visible:outline-none', className)}
      {...props}
    >
      {children}
    </div>
  );
});
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };
